/**
 * Self-checkout flow descriptors.
 *
 * One declarative object per self-checkout flow (Journey vs direct-lab) so
 * flow divergence lives in ONE place instead of scattered
 * `isDirectLabsCheckoutPath` branches. Presentational primitives
 * (CheckoutProgressHeader, AvailabilityGate) consume the descriptor via thin
 * per-surface wrappers; genuinely flow-specific pieces stay their own
 * components and are only *selected* through the descriptor (escape hatch).
 *
 * Keep this module free of hooks, locales, and network calls — descriptors
 * are plain data + pure functions so both the unauthenticated and
 * authenticated bundles can share them.
 */

type SelfCheckoutFlow = "journey" | "directLab";

/**
 * Superset of stepper steps across flows. `SELF_CHECKOUT_STATES` in
 * `@checkout/shared/data/types` remains the URL-step model; these are the
 * *display* steps the progress header renders.
 */
type SelfCheckoutStepKey = "patientInfo" | "address" | "sampleCollection" | "payment";

interface SelfCheckoutFlowStepContext {
  /** odyssey_journeys_mobile_phlebotomy — gates the Journey sample-collection step. */
  isMobilePhlebotomyEnabled: boolean;
}

type SelfCheckoutEntryField = "email" | "zip";

interface SelfCheckoutFlowDescriptor {
  flow: SelfCheckoutFlow;
  /**
   * Ordered stepper steps for the flow. Encodes ALL step gating so wrappers
   * never re-derive it:
   * - journey: sampleCollection appears when mobile phlebotomy is enabled
   * - directLab: never surfaces phlebotomy step chrome (BUT-1112); BUT-1119
   *   may add a kit-driven sample-collection step here later
   */
  resolveSteps: (context: SelfCheckoutFlowStepContext) => SelfCheckoutStepKey[];
  /** Fields the unauthenticated availability/entry form collects. */
  entryFields: SelfCheckoutEntryField[];
  /**
   * Whether the entry step runs the location (zip -> state -> patient-state
   * restriction) gate before starting auth. The gate reuses
   * Labs::DirectCheckout::Eligibility server-side — descriptors only declare
   * *that* it runs, never *how*.
   */
  requiresLocationGate: boolean;
}

const JOURNEY_FLOW: SelfCheckoutFlowDescriptor = {
  flow: "journey",
  resolveSteps: ({ isMobilePhlebotomyEnabled }) =>
    isMobilePhlebotomyEnabled
      ? ["patientInfo", "address", "sampleCollection", "payment"]
      : ["patientInfo", "address", "payment"],
  entryFields: ["email", "zip"],
  // Journey runs its own zip serviceability check (CheckForJourneysAvailability:
  // package supported_states / labs zone / Quest proximity), not the direct-lab
  // state-restriction gate.
  requiresLocationGate: false,
};

const DIRECT_LAB_FLOW: SelfCheckoutFlowDescriptor = {
  flow: "directLab",
  // Direct-lab: never surface mobile or In-Office phlebotomy step chrome
  // (BUT-1112). BUT-1119 may reintroduce a specialty-kit sample-collection
  // step without those options — extend here, not in wrappers.
  resolveSteps: () => ["patientInfo", "address", "payment"],
  entryFields: ["email", "zip"],
  requiresLocationGate: true,
};

const SELF_CHECKOUT_FLOW_DESCRIPTORS: Record<SelfCheckoutFlow, SelfCheckoutFlowDescriptor> = {
  journey: JOURNEY_FLOW,
  directLab: DIRECT_LAB_FLOW,
};

const getSelfCheckoutFlowDescriptor = (flow: SelfCheckoutFlow): SelfCheckoutFlowDescriptor =>
  SELF_CHECKOUT_FLOW_DESCRIPTORS[flow];

export type {
  SelfCheckoutEntryField,
  SelfCheckoutFlow,
  SelfCheckoutFlowDescriptor,
  SelfCheckoutFlowStepContext,
  SelfCheckoutStepKey,
};
export { getSelfCheckoutFlowDescriptor };
